import React, { useEffect, useMemo, useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { generatePageTitle } from '@elara/client-core/lib/page-title';

import { useRouterState } from '#states/hooks.js';
import { useTypingEffect } from '#ui/hooks/useTypingEffect.js';
import { Logo } from '#ui/index.js';
import { LinkButton } from '#views/Router.js';

export function PageNotFound() {
  const routerState = useRouterState();

  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    routerState.setPageTitle(generatePageTitle('Page not found'));
  }, [routerState]);

  const haikus = [
    'What is not found here,\nExists somewhere else perhaps,\nOr never existed.',
    'Oops! Wrong turn taken,\nLost in cyberspace again,\nTime to find home base.',
    'Foggy web of dreams,\nWhere destinations dissolve,\nInto endless void.',
    'Lost paths in the mist,\nWhispers of what once was here,\nGone, yet memories.',
    'Journey interrupted,\nLike a train that never came,\nDestinations wait.',
    'Road less traveled by,\nLeads to pages not yet built,\nAdventure begins here.',
  ];

  const randomHaiku = useMemo(() => {
    return haikus[Math.floor(Math.random() * haikus.length)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this only runs once

  const { displayedText, isTyping } = useTypingEffect(randomHaiku || '', true, {
    speed: 15, // Slower, more contemplative speed
    onComplete: () => {
      // Show buttons after the haiku is complete
      setTimeout(() => setShowButtons(true), 500);
    },
  });

  // Split lines once instead of three times per render
  const lines = useMemo(() => displayedText.split('\n'), [displayedText]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      <div className="text-left w-full max-w-[260px] min-h-[230px]">
        <Logo variant="symbol" width={24} />
        <h1 className="mt-4 text-2xl font-semibold font-display">Page not found</h1>

        <div className="mt-2 text-muted-foreground min-h-[4rem]">
          {lines.map((line, index) => (
            <p key={index} className={isTyping && index === lines.length - 1 ? 'inline' : ''}>
              {line}
              {isTyping && index === lines.length - 1 && <span className="animate-pulse">|</span>}
            </p>
          ))}
        </div>

        {showButtons && (
          <div className="flex flex-col gap-3 sm:flex-row mt-6 animate-in fade-in duration-500">
            <LinkButton to={ROUTES.HOME} variant="outline">
              Home
            </LinkButton>
          </div>
        )}
      </div>
    </div>
  );
}
